/* eslint-disable no-unused-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement, useContext } from 'react';
import {
  InputContainer,
  InputText,
  Accordion,
  FileInput,
  Tooltip,
  Icon,
} from '@betrybe/ada-react';
import {
  cepMask,
  color,
  hasIcon,
  residenceTypesOptions,
  textColor,
} from '@/utils';
import { CheckboxInput, SelectInput } from '@/components/molecules';
import type { getGuarantorsType } from '@/services/pre-enrollment/types';
import { useClient } from '@/hooks';
import { StudentContext } from '@/contexts';
import {
  setCandidateAddress,
  setFirstGuarantorPersonAddress,
  setInitialValues,
} from './utils/setAddressUtils';
import StatesEnum from '@/utils/enums/StatesEnum';
import {
  checkAccordionChecked,
  checkAccordionErrors,
} from './utils/checkAccordionErrors';
import { addressDocumentUrlDownload } from './utils/const';

type AddressFormTypes = {
  formik: any;
  handleSearchCep: (value: string) => void;
  handleFileUpload: (
    event: { file: File }[],
    field: string,
    fieldName: string,
  ) => void;
  proofOfAddressRef: React.LegacyRef<HTMLDivElement>;
  formType: 'candidate' | 'guarantor';
  firstGuarantor?: getGuarantorsType | undefined;
  getGuarantorsService?: () => void;
  callTrack?: (
    eventName: string,
    component: string,
    guarantor?: getGuarantorsType | undefined,
  ) => void;
};

function AddressForm(props: AddressFormTypes): ReactElement {
  const { currentState } = useClient();
  const { state } = useContext(StudentContext);
  const {
    formik,
    proofOfAddressRef,
    handleSearchCep,
    handleFileUpload,
    formType,
    firstGuarantor,
    getGuarantorsService,
    callTrack,
  } = props;

  const guarantorPerson: getGuarantorsType | undefined = firstGuarantor;

  const accordionError: boolean = checkAccordionErrors({ formik });
  const accordionChecked: boolean = checkAccordionChecked({ formik });

  const isFirstGuarantorEditPage =
    window.location.pathname.includes('/register/first-person-guarantor') ||
    window.location.pathname.includes('/register/guarantor-form');

  const textHeader = (
    <div className="flex flex-row space-x-2 items-center">
      {hasIcon({ accordionChecked, accordionError })}
      <p
        className={`bold text-base ${textColor({
          accordionChecked,
          accordionError,
        })}`}
      >
        Endereço
      </p>
    </div>
  );

  if (
    (currentState === StatesEnum.SECOND_GUARANTOR_REGISTRATION_FORM ||
      currentState === StatesEnum.SECOND_GUARANTOR_REGISTRATION) &&
    !guarantorPerson
  ) {
    getGuarantorsService && getGuarantorsService();
  }

  const downloadTracker = () => {
    callTrack &&
      callTrack(
        'Address Statement Downloaded',
        'Fez download da declaração de residência',
      );
  };

  return (
    <Accordion
      header={textHeader}
      markerColor={color({ accordionChecked, accordionError })}
    >
      <div className="flex flex-col m-6 space-y-5">
        {formType === 'guarantor' && (
          <div>
            <InputContainer label="">
              {(inputProps) => (
                <CheckboxInput
                  {...inputProps}
                  id="same-as-my-address"
                  label="Igual ao meu endereço"
                  name="sameAsMine"
                  isControlled={false}
                  disabled={false}
                  checked={formik.values.sameAsMine === 'true'}
                  onChange={async (event) => {
                    if (event.target.checked) {
                      setCandidateAddress(formik, state.clientData);
                      formik.setValues({
                        ...formik.values,
                        sameAsFirstGuarantor: 'false',
                        sameAsMine: 'true',
                      });
                    } else {
                      setInitialValues(formik);
                      formik.setFieldValue('sameAsMine', 'false');
                    }
                    callTrack &&
                      callTrack(
                        'Same Address Submitted',
                        'Igual ao meu endereço',
                      );
                  }}
                  value="true"
                  data-testid="sameAsMine"
                />
              )}
            </InputContainer>
            {firstGuarantor && !isFirstGuarantorEditPage && (
              <InputContainer label="">
                {(inputProps) => (
                  <CheckboxInput
                    {...inputProps}
                    id="same-as-first-guarantor-address"
                    label="Igual ao endereço da primeira pessoa avalista"
                    name="sameAsFirstGuarantor"
                    isControlled={false}
                    disabled={false}
                    checked={formik.values.sameAsFirstGuarantor === 'true'}
                    onChange={async (event) => {
                      if (event.target.checked) {
                        setFirstGuarantorPersonAddress(formik, firstGuarantor);
                        formik.setValues({
                          ...formik.values,
                          sameAsMine: 'false',
                          sameAsFirstGuarantor: 'true',
                        });
                      } else {
                        setInitialValues(formik);
                        formik.setFieldValue('sameAsFirstGuarantor', 'false');
                      }
                      callTrack &&
                        callTrack(
                          'Same Guarantor Address Submitted',
                          'Igual ao endereço da primeira pessoa avalista',
                        );
                    }}
                    value="true"
                    data-testid="sameAsFirstGuarantor"
                  />
                )}
              </InputContainer>
            )}
          </div>
        )}

        <InputContainer
          label="CEP"
          error={formik.touched.zipcode && formik.errors.zipcode}
        >
          {(inputProps) => (
            <div>
              <p className="register-container__input-help-text">
                Verifique seu CEP através do{' '}
                <a
                  href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                  className="underline text-royal-blue-500"
                  target="_blank"
                  rel="noreferrer"
                >
                  Portal dos Correios
                </a>
              </p>
              <div>
                <InputText
                  {...inputProps}
                  id="input-register-postCode"
                  type="text"
                  name="zipcode"
                  placeholder="00000-000"
                  onChange={(e) => {
                    const value = cepMask(e.target.value);
                    formik.setFieldValue('zipcode', value);
                    if (value.length === 9) {
                      handleSearchCep(value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.zipcode}
                  data-testid="postCode"
                  disabled={
                    formik.values.sameAsMine === 'true' ||
                    formik.values.sameAsFirstGuarantor === 'true'
                  }
                />
              </div>
            </div>
          )}
        </InputContainer>

        <div className="register-container__filds-group--inputs-row">
          <InputContainer
            label="Estado"
            error={formik.touched.state && formik.errors.state}
          >
            {(inputProps) => (
              <div>
                <div>
                  <InputText
                    {...inputProps}
                    id="input-register-state"
                    type="text"
                    name="state"
                    placeholder="SP"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.state}
                    data-testid="state"
                    disabled={
                      formik.values.sameAsMine === 'true' ||
                      formik.values.sameAsFirstGuarantor === 'true'
                    }
                  />
                </div>
              </div>
            )}
          </InputContainer>
          <InputContainer
            label="Cidade"
            error={formik.touched.city && formik.errors.city}
          >
            {(inputProps) => (
              <div>
                <div>
                  <InputText
                    {...inputProps}
                    id="input-register-city"
                    type="text"
                    name="city"
                    placeholder="São Paulo"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.city}
                    data-testid="city"
                    disabled={
                      formik.values.sameAsMine === 'true' ||
                      formik.values.sameAsFirstGuarantor === 'true'
                    }
                  />
                </div>
              </div>
            )}
          </InputContainer>
        </div>
        <InputContainer
          label="Bairro"
          error={formik.touched.neighborhood && formik.errors.neighborhood}
        >
          {(inputProps) => (
            <div>
              <div>
                <InputText
                  {...inputProps}
                  id="input-register-neighborhood"
                  type="text"
                  name="neighborhood"
                  placeholder="Meu bairro é..."
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.neighborhood}
                  data-testid="neighborhood"
                  disabled={
                    formik.values.sameAsMine === 'true' ||
                    formik.values.sameAsFirstGuarantor === 'true'
                  }
                />
              </div>
            </div>
          )}
        </InputContainer>
        <InputContainer
          label="Logradouro"
          error={formik.touched.address && formik.errors.address}
        >
          {(inputProps) => (
            <div>
              <div>
                <InputText
                  {...inputProps}
                  id="input-register-address"
                  type="text"
                  name="address"
                  placeholder="Minha rua ou avenida é..."
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.address}
                  data-testid="address"
                  disabled={
                    formik.values.sameAsMine === 'true' ||
                    formik.values.sameAsFirstGuarantor === 'true'
                  }
                />
              </div>
            </div>
          )}
        </InputContainer>
        <InputContainer label="Tipo de Residência" error="">
          {(inputProps) => (
            <div>
              <div>
                <SelectInput
                  {...inputProps}
                  id="residenceType"
                  onChange={formik.handleChange}
                  placeholder="Apartamento"
                  value={formik.values.residence_type}
                  name="residence_type"
                  options={residenceTypesOptions.map(({ value, label }) => ({
                    label,
                    value,
                  }))}
                  errorMessage={
                    formik.touched.residence_type &&
                    formik.errors.residence_type
                      ? formik.errors.residence_type
                      : ''
                  }
                  data-testid="residenceType"
                  disabled={
                    formik.values.sameAsMine === 'true' ||
                    formik.values.sameAsFirstGuarantor === 'true'
                  }
                />
              </div>
            </div>
          )}
        </InputContainer>
        <div className="register-container__filds-group--inputs-row">
          <InputContainer
            label="Número"
            error={
              formik.touched.residence_number && formik.errors.residence_number
            }
          >
            {(inputProps) => (
              <div>
                <div>
                  <InputText
                    {...inputProps}
                    id="input-register-number"
                    type="text"
                    name="residence_number"
                    placeholder="00"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.residence_number}
                    data-testid="number"
                    disabled={
                      formik.values.sameAsMine === 'true' ||
                      formik.values.sameAsFirstGuarantor === 'true'
                    }
                  />
                </div>
              </div>
            )}
          </InputContainer>
          <InputContainer
            label="Complemento"
            error={
              formik.touched.address_complement &&
              formik.errors.address_complement
            }
          >
            {(inputProps) => (
              <div>
                <div>
                  <InputText
                    {...inputProps}
                    id="input-register-complement"
                    type="text"
                    name="address_complement"
                    placeholder="Ap 00 bloco 0"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.address_complement}
                    data-testid="complement"
                    disabled={
                      formik.values.sameAsMine === 'true' ||
                      formik.values.sameAsFirstGuarantor === 'true'
                    }
                  />
                </div>
              </div>
            )}
          </InputContainer>
        </div>
        <InputContainer
          label=""
          error={formik.touched.proofOfAddress && formik.errors.proofOfAddress}
        >
          {(inputProps) => (
            <div ref={proofOfAddressRef}>
              <div className="register-container__filds-group">
                <FileInput
                  {...inputProps}
                  buttonLabel="Adicionar arquivo"
                  id="proofOfAddress"
                  name="proofOfAddress"
                  helpText={
                    <div className="text-sm">
                      <p>Precisa estar em seu nome no formato .jpg ou .pdf.</p>{' '}
                      <p>Não tem nenhum comprovante em seu nome?</p>
                      <p>
                        {' '}
                        Envie{' '}
                        <a
                          href={addressDocumentUrlDownload}
                          className="text-royal-blue-500 cursor-pointer underline"
                          rel="noreferrer"
                          onClick={downloadTracker}
                        >
                          {' '}
                          essa declaração
                        </a>{' '}
                        preenchida e assinada a punho.
                      </p>
                    </div>
                  }
                  label={
                    <>
                      {formType === 'candidate'
                        ? 'Comprovante de endereço em seu nome'
                        : 'Comprovante de endereço'}
                      <Tooltip
                        title="Aceitamos conta do cartão de crédito, água, luz, gás encanado, TV, Internet, telefone fixo ou celular. Arquivos de até 5Mb."
                        backgroundColor="black-800"
                      >
                        <Icon
                          name="help"
                          className="register-container__tooltip-icon"
                          width={18}
                        />
                      </Tooltip>
                    </>
                  }
                  className="mt-8"
                  multiple={false}
                  onChange={async (e) => {
                    await handleFileUpload(
                      e,
                      'proofOfAddress',
                      'proof_of_residency',
                    );
                  }}
                  accept=".jpg,.jpeg,.pdf"
                  items={
                    formik.values.proofOfAddress && [
                      formik.values.proofOfAddress,
                    ]
                  }
                  onDelete={() => formik.setFieldValue('proofOfAddress', null)}
                  data-testid="proofOfAddress"
                />
              </div>
            </div>
          )}
        </InputContainer>
      </div>
    </Accordion>
  );
}

AddressForm.defaultProps = {
  firstGuarantor: undefined,
  callTrack: undefined,
  getGuarantorsService: undefined,
};

export default AddressForm;
